import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { StaticMessage } from '@shared/enums';
import { FileUpload } from '@shared/models';
import {
  Constants,
  getStaticMessage,
  hasInvalidFileNameChars,
} from '@shared/utils';
import { FileAcceptDisplayPipe, FileUploadData } from '.';
import { ConfirmDialogComponent } from '..';

@Injectable({
  providedIn: 'root',
})
export class FileUploadService {
  constructor(
    private dialog: MatDialog,
    private fileAcceptDisplayPipe: FileAcceptDisplayPipe
  ) {}

  processData(data: FileUpload[]): FileUploadData[] {
    if (!data) {
      return [];
    }

    return (data.copy() as FileUploadData[]).map((item) => {
      if (!item.extension) {
        item.extension = this.getExt(item.name);
        item.name =
          item.name.lastIndexOf('.') !== -1
            ? item.name.slice(0, item.name.lastIndexOf('.'))
            : item.name;

        if (!item.displayName) {
          item.displayName = item.name;
        }

        item.displayName = item.extension
          ? `${item.displayName}.${item.extension}`
          : item.displayName;

        item.commentSubtitle = item.name;
      }

      return item;
    });
  }

  processFile(data: FileUpload): FileUploadData {
    if (!data) {
      return null;
    }

    return this.processData([data])[0];
  }

  processAcceptFiles(accept: string): string {
    if (!accept) {
      return null;
    }

    return accept + ',' + accept.toUpperCase();
  }

  /**
   * @description Determines if a file is an image based on the file type
   * @returns bool
   */
  isImage(fileType: string): boolean {
    if (!fileType || fileType === 'image/gif') {
      return false;
    }

    return fileType.startsWith('image/');
  }

  validateFiles(
    files: FileList,
    accept: string,
    completeAccept: string,
    isAppOnline: boolean,
    maxSize: number,
    sizeType?: string
  ): boolean {
    if (!isAppOnline) {
      this.dialog.open(ConfirmDialogComponent, {
        ...Constants.DialogOptions,
        data: {
          title: $localize`:@@shared.fileUploadService.titleYouAreOffline:You are offline`,
          message: $localize`:@@shared.fileUploadService.txtFileUploadWorksOnlyInOnline:File upload only works when online.`,
          buttonLabel: getStaticMessage(StaticMessage.OkButton),
          hideCancelButton: true,
        },
      });

      return false;
    }

    for (let i = 0; i < files.length; i++) {
      const file = files.item(i);

      if (!file) {
        this.dialog.open(ConfirmDialogComponent, {
          ...Constants.DialogOptions,
          data: {
            title: getStaticMessage(StaticMessage.UploadErrorTitle),
            message: $localize`:@@shared.fileUploadService.txtUploadErrorGeneric:An error has occurred while uploading your file, please try again or contact your administrator.`,
            buttonLabel: getStaticMessage(StaticMessage.OkButton),
            hideCancelButton: true,
          },
        });

        return false;
      }

      if (!this.validateAcceptedFiles(completeAccept, file.name)) {
        this.dialog.open(ConfirmDialogComponent, {
          ...Constants.DialogOptions,
          data: {
            title: getStaticMessage(StaticMessage.UploadErrorTitle),
            message: $localize`:@@shared.fileUploadService.txtUploadErrorFileType:File type must be ${this.fileAcceptDisplayPipe.transform(
              accept
            )}:acceptFiles:`,
            buttonLabel: getStaticMessage(StaticMessage.OkButton),
            hideCancelButton: true,
          },
        });

        return false;
      }

      const maxSizeMessage = this.validateSize(file.size, maxSize, sizeType);
      if (maxSizeMessage) {
        this.dialog.open(ConfirmDialogComponent, {
          ...Constants.DialogOptions,
          data: {
            title: getStaticMessage(StaticMessage.UploadErrorTitle),
            message: $localize`:@@shared.fileUploadService.txtUploadErrorFileSize:You cannot upload a file larger than ${maxSizeMessage}:maxSize:`,
            buttonLabel: getStaticMessage(StaticMessage.OkButton),
            hideCancelButton: true,
          },
        });

        return false;
      }
    }

    return true;
  }

  validateFileName(fileName: string): boolean {
    return !hasInvalidFileNameChars(fileName);
  }

  getExt(name: string): string {
    if (name) {
      const parts = name.split('.');

      if (parts.length > 1) {
        return parts[parts.length - 1];
      }
    }

    return null;
  }

  //#region Private methods

  private validateAcceptedFiles(accept: string, fileName: string): boolean {
    if (!accept) {
      return true;
    }

    const extensions = accept.split(',');
    return extensions.some(
      (item) => this.getExt(item) === this.getExt(fileName)
    );
  }

  private validateSize(
    fileSize: number,
    maxSize: number,
    sizeType?: string
  ): string {
    if (maxSize) {
      if (fileSize >= maxSize) {
        return this.convertToKBOrMB(maxSize, sizeType);
        // return true;
      }
    } else {
      if (fileSize >= 5300000) {
        return '5 MB.';
        // return true;
      }
    }

    return null;
  }

  private convertToKBOrMB(sizeInBytes: number, sizeType?: string): string {
    switch (sizeType) {
      case 'KB':
        return '</br>' + Math.round(sizeInBytes / 1024) + ' KB.';

      default:
        return '</br>' + Math.round(sizeInBytes / (1024 * 1024)) + ' MB.';
    }
  }

  //#endregion
}
